var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { appActions } from '@app';
import { error } from '@sonar-software/xuder';
import isEqual from 'lodash/isEqual';
import { useAction } from './useAction';
import { useOnUnmount } from './useOnUnmount';
import { ErrorOrFailStatusCode, getQueryString, sonarHttpGet } from '@sonar-software/sonar-http';
import { useCallback, useEffect, useRef, useState } from 'react';
export function useApiResource(uri, queryParams, manual) {
    const [loading, setLoading] = useState(!manual);
    const [resource, setResource] = useState();
    const [response, setResponse] = useState();
    const queryParamsRef = useRef();
    const isMountedRef = useRef(true);
    const requestCanceled = useRef(false);
    const maintenance = useAction(appActions.updateApplicationMaintenance);
    const logError = useAction(error);
    useOnUnmount(() => {
        isMountedRef.current = false;
    });
    const request = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (!uri || !isMountedRef.current) {
            return;
        }
        requestCanceled.current = false;
        setLoading(true);
        const cleanedUri = uri.startsWith('/') ? uri.slice(1) : uri;
        const queryString = getQueryString(queryParams);
        const targetUri = `/api/${cleanedUri}${cleanedUri.includes('?') ? '&' : '?'}${queryString}`;
        try {
            const apiResponse = yield sonarHttpGet({
                targetUri
            });
            if (apiResponse.status === ErrorOrFailStatusCode.ServiceUnavailable) {
                maintenance({ locked: true });
            }
            if (!isMountedRef.current || requestCanceled.current) {
                return;
            }
            setResponse(apiResponse);
            if (apiResponse.data) {
                setResource(apiResponse.data);
                setLoading(false);
            }
            else {
                throw new Error(`Failure retrieving resource at: ${targetUri}`);
            }
        }
        catch (ex) {
            logError(ex.message);
        }
    }), [logError, maintenance, queryParams, uri]);
    const reset = useCallback(() => {
        if (loading) {
            requestCanceled.current = true;
        }
        setResponse(undefined);
        setResource(undefined);
        setLoading(false);
    }, [loading]);
    useEffect(() => {
        if (manual || !uri || !isMountedRef.current) {
            return;
        }
        if (queryParams && isEqual(queryParams, queryParamsRef.current)) {
            return;
        }
        queryParamsRef.current = queryParams;
        request();
    }, [logError, queryParams, manual, uri, request]);
    return {
        loading,
        request,
        reset,
        resource,
        response
    };
}
